import {
    Box,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { useIntl } from 'react-intl';
import { useRecoilState } from 'recoil';
import CompactToggle from '~/components/toolbar/shared/CompactToggle';
import GhostLineSample from '~/components/Viewer/GhostLineSample';
import {
    GHOST_DEPTHS,
    GHOST_SIDE_DIRECTIONS,
    GHOST_SIDES,
    GhostDepth,
    GhostSide,
} from '~/engine/core/render/renderers/slices/adjacentSliceGhost';
import {
    adjacentSliceGhostDepthsState,
    adjacentSliceGhostsEnabledState,
} from '~/state/atoms/viewerAtoms';

/**
 * A compact toggle-button group, shorter than a field, so it sits in a
 * row without setting the row's height.
 */
const compactToggleGroupSx = {
    flexShrink: 0,
    '& .MuiToggleButton-root': {
        height: 'auto',
        py: 0.5,
        px: 1,
        fontSize: 11,
        lineHeight: 1,
    },
};

/** The message naming each side of the displayed slice by its line pattern. */
const SIDE_LABEL_ID: Readonly<Record<GhostSide, string>> = {
    dashed: 'toolbar.adjacentSliceGhosts.dashedSide',
    dotted: 'toolbar.adjacentSliceGhosts.dottedSide',
};

/**
 * Body of the adjacent-slice ghosts tool: the switch, and per side which of
 * the three nearest slices are ghosted. Sides are named by line pattern and
 * list the directions it stands for, since the anatomy depends on the view.
 * Depths are disabled while the switch is off.
 */
export default function AdjacentSliceGhostsOptions() {
    const [enabled, setEnabled] = useRecoilState(
        adjacentSliceGhostsEnabledState
    );
    const [depths, setDepths] = useRecoilState(adjacentSliceGhostDepthsState);

    return (
        <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
            data-testid="adjacent-slice-ghosts-options"
        >
            <CompactToggle
                labelId="toolbar.adjacentSliceGhosts.enable"
                tooltipId="toolbar.adjacentSliceGhosts.enable.tooltip"
                value={enabled}
                onChange={setEnabled}
                dataTestId="adjacent-slice-ghosts-enable"
                tooltipPlacement="left"
                fullWidth
            />
            {GHOST_SIDES.map((side) => (
                <DepthRow
                    key={side}
                    side={side}
                    depths={depths[side]}
                    disabled={!enabled}
                    onChange={(chosen) =>
                        setDepths((previous) => ({
                            ...previous,
                            [side]: chosen.toSorted((a, b) => a - b),
                        }))
                    }
                />
            ))}
        </Box>
    );
}

/** One side of the displayed slice: its line sample, its name, and which slices on it are ghosted. */
function DepthRow({
    side,
    depths,
    disabled,
    onChange,
}: {
    side: GhostSide;
    depths: ReadonlyArray<GhostDepth>;
    disabled: boolean;
    onChange: (depths: ReadonlyArray<GhostDepth>) => void;
}) {
    const intl = useIntl();
    const label = intl.formatMessage(
        { id: SIDE_LABEL_ID[side] },
        {
            directions: intl.formatList(GHOST_SIDE_DIRECTIONS[side], {
                type: 'disjunction',
            }),
        }
    );
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                opacity: disabled ? 0.5 : 1,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <GhostLineSample side={side} />
                <Typography fontSize={11} noWrap sx={{ userSelect: 'none' }}>
                    {label}
                </Typography>
            </Box>
            <ToggleButtonGroup
                size="small"
                color="primary"
                value={depths}
                disabled={disabled}
                onChange={(_, chosen: GhostDepth[]) => onChange(chosen)}
                aria-label={label}
                sx={compactToggleGroupSx}
            >
                {GHOST_DEPTHS.map((depth) => (
                    <ToggleButton
                        key={depth}
                        value={depth}
                        aria-label={intl.formatMessage(
                            { id: 'toolbar.adjacentSliceGhosts.depth' },
                            { depth }
                        )}
                        data-testid={`adjacent-slice-ghosts-${side}-${depth}`}
                    >
                        {depth}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
        </Box>
    );
}
